import { useCallback } from "react";
import { toast } from "react-toastify";
import { useClinicalRegisterContext } from "@/contexts/ClinicalRegisterContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { practitionerRoleReference } from "../../invites/helpers/practitionerRoleReferences";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { findTeleconsultorAgenda } from "../../affiliate/services/providerAgendaService";
import { inviteErrorMessage } from "../../invites/helpers/inviteErrorMessage";
import { InviteActionError } from "../../invites/services/inviteActionError";
import { useTeleconsultoriaProfile } from "../../profile/TeleconsultoriaProfileContext";
import type { TeleconsultoriaActor } from "../../types";
import { createTeleconsultoriaAppointment } from "../services/createTeleconsultoriaAppointment";
import { resolveRequesterHealthcareService } from "../services/resolveRequesterHealthcareService";
import type { BookingPatient, BookingTarget } from "../types";

interface BookParams {
  target: BookingTarget;
  patient: BookingPatient;
  requester: TeleconsultoriaActor;
}

/**
 * Marca a teleconsultoria (afiliada → horário da prestadora).
 * "Agora": localiza a agenda com horários do teleconsultor e cria o slot nela.
 * Quando o próprio usuário é o solicitante no modo "Agora", já abre o atendimento.
 */
export function useTeleconsultoriaBooking() {
  const { access } = useSessionContext();
  const { profile } = useTeleconsultoriaProfile();
  const { verifyEncounter } = useClinicalRegisterContext();
  const providerToken = profile.kind === "affiliate" ? profile.providerToken : "";
  const affiliateOrganizationId = access?.organizationId || "";

  const bookTask = useAsyncTask({
    task: useCallback(
      async ({ target, patient, requester }: BookParams) => {
        if (!providerToken) {
          throw new InviteActionError("Sua clínica não está afiliada a uma prestadora.");
        }
        // Quem convida (requester da Task): o solicitante, na organização afiliada.
        const hostRoleReference = practitionerRoleReference({
          organizationId: affiliateOrganizationId,
          practitionerReference: requester.reference,
        });
        // HCS de quem convida (host). Sem ele, não marca (mensagem pronta).
        const requesterHealthcareService = await resolveRequesterHealthcareService({
          requester,
          affiliateOrganizationId,
        });
        if (target.mode === "slot") {
          return createTeleconsultoriaAppointment({
            agenda: target.agenda,
            slot: { id: target.slotId, start: target.start, end: target.end },
            patient,
            requester,
            requesterHealthcareService,
            providerToken,
            hostRoleReference,
          });
        }
        const agenda = await findTeleconsultorAgenda({
          providerToken,
          practitionerReference: target.teleconsultor.reference,
        });
        if (!agenda) {
          throw new InviteActionError(
            "Este teleconsultor não tem agenda de teleconsultoria com horários.",
          );
        }
        return createTeleconsultoriaAppointment({
          agenda,
          patient,
          requester,
          requesterHealthcareService,
          providerToken,
          hostRoleReference,
        });
      },
      [providerToken, affiliateOrganizationId],
    ),
  });

  const { run } = bookTask;
  // Devolve o Appointment criado (quem abriu destaca a linha nova); undefined = falhou.
  const book = useCallback(
    async (params: BookParams & { selfReference: string }): Promise<fhir5.Appointment | undefined> => {
      try {
        const appointment = await run(params);
        toast.success("Teleconsultoria marcada. O teleconsultor recebeu o convite para aceitar.");
        // "Agora" com o próprio usuário como solicitante: entra direto no atendimento.
        // O teleconsultor entra quando aceitar o convite (igual ao convite do atendimento).
        if (params.target.mode === "now" && params.requester.reference === params.selfReference) {
          await verifyEncounter(appointment);
        }
        return appointment;
      } catch (error) {
        console.error("useTeleconsultoriaBooking", error);
        toast.error(inviteErrorMessage(error, "Não foi possível marcar a teleconsultoria."));
        return undefined;
      }
    },
    [run, verifyEncounter],
  );

  return { book, isBooking: bookTask.loading };
}
